import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { CANVAS_ID } from "../../../librettoEditor/config/config";
import { TextClip } from "../../../engine/clips/text";
import type { CaptionsTextStyle } from "../../../engine/clips/captions/captions.types";

const TextClipEditorOverlay: React.FC = observer(() => {
    const editingClipId = ApplicationStore.editingTextClipId;
    const isCropMode = ApplicationStore.isCropMode;
    const selectedScope = ApplicationStore.sceneEditScope;
    const didCommitRef = useRef(false);

    const clip = useMemo(() => {
        if (!editingClipId) return null;
        return CompositionService.getClipById(editingClipId) as any;
    }, [editingClipId]);

    const isTextClip = Boolean(clip && String(clip.type) === "text");

    const sceneKey = useMemo(() => {
        if (!clip || String(clip.type) !== "text") return null;
        const spans = CompositionService.clipSpansMultipleScenes(clip);
        if (selectedScope !== "scene" || !spans) return null;

        const scriptClip: any = CompositionService.getScriptLayerClip?.();
        if (!scriptClip) return null;
        const scenes: any[] = Array.isArray(scriptClip.getScenes?.()) ? scriptClip.getScenes() : [];
        if (!scenes.length) return null;

        const idxExplicit = ApplicationStore.selectedSceneIndex;
        if (idxExplicit != null) {
            const scene = scenes.find((s: any) => Number(s?.index) === Number(idxExplicit)) ?? scenes[idxExplicit];
            return scene?.startSplitId ? String(scene.startSplitId) : "__start__";
        }

        const t = Number(CompositionStateStore.currentTime ?? 0);
        const scene =
            scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
            scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
            scenes[scenes.length - 1];
        return scene?.startSplitId ? String(scene.startSplitId) : "__start__";
    }, [clip?.id, selectedScope, ApplicationStore.selectedSceneIndex, CompositionStateStore.currentTime]);

    const effectiveStyleAndText = useMemo(() => {
        if (!clip || String(clip.type) !== "text") return null;
        const baseText = String((clip as any).text ?? "");
        const baseStyle: CaptionsTextStyle = (clip as any).textStyle ?? {};
        const overrides = (clip as any).sceneStyleOverrides ?? {};
        const ov = sceneKey ? overrides[sceneKey] : undefined;
        const text = String(ov?.text ?? baseText);
        const style: CaptionsTextStyle = { ...baseStyle, ...(ov?.textStyle ?? {}) };
        const align = String(ov?.textAlign ?? (clip as any).textAlign ?? "center");
        const textAlign = align === "left" || align === "right" ? align : "center";
        return { text, style, textAlign };
    }, [clip?.id, sceneKey]);

    const [pos, setPos] = useState<{ left: number; top: number; width: number; height: number; scaleX: number; scaleY: number } | null>(null);
    const [editorHeightPx, setEditorHeightPx] = useState<number | null>(null);
    const [value, setValue] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Initialize textarea value when editing starts.
    useEffect(() => {
        if (!editingClipId || !isTextClip || !effectiveStyleAndText) return;
        setValue(effectiveStyleAndText.text);
        setEditorHeightPx(null);
        // Focus after paint.
        requestAnimationFrame(() => {
            try {
                textareaRef.current?.focus();
                textareaRef.current?.select();
            } catch {}
        });
    }, [editingClipId, isTextClip]);

    // Hide the Pixi text while editing so the textarea doesn't look "duplicated".
    useEffect(() => {
        if (!editingClipId || !clip || String(clip.type) !== "text") return;
        try {
            (clip as TextClip).setEditingMode(true);
        } catch {}
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        return () => {
            try {
                (clip as TextClip).setEditingMode(false);
            } catch {}
            try { CompositionService.recomputeFrame(); } catch {}
            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        };
    }, [editingClipId, clip?.id]);

    // Auto-grow/shrink editor height as the user types so newlines appear immediately.
    useLayoutEffect(() => {
        if (!editingClipId || !isTextClip || !pos) return;
        const el = textareaRef.current;
        if (!el) return;

        // Temporarily allow auto sizing to measure correctly (including shrink).
        try {
            el.style.height = "auto";
        } catch {}

        const measured = Math.ceil(Number(el.scrollHeight ?? pos.height));
        const minH = Math.max(36, Number(pos.height ?? 0));
        const nextH = Math.max(minH, measured);
        setEditorHeightPx((prev) => (prev != null && Math.abs(prev - nextH) < 1 ? prev : nextH));
    }, [editingClipId, isTextClip, pos?.width, pos?.height, value]);

    // Compute overlay position from clip bounds.
    useEffect(() => {
        const compute = () => {
            if (!editingClipId || !clip || String(clip.type) !== "text") {
                setPos(null);
                return;
            }
            const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;
            const comp = CompositionService.composition as any;
            if (!canvas || !comp) {
                setPos(null);
                return;
            }

            const bounds = typeof (clip as any).view?.getBounds === "function" ? (clip as any).view.getBounds() : null;
            if (!bounds) {
                setPos(null);
                return;
            }

            const canvasRect = canvas.getBoundingClientRect();
            const compW = Number(comp.settings?.width ?? comp.width ?? 1920);
            const compH = Number(comp.settings?.height ?? comp.height ?? 1080);
            if (!(compW > 0 && compH > 0)) {
                setPos(null);
                return;
            }

            const scaleX = canvasRect.width / compW;
            const scaleY = canvasRect.height / compH;
            const left = canvasRect.left + Number(bounds.x ?? 0) * scaleX;
            const top = canvasRect.top + Number(bounds.y ?? 0) * scaleY;
            const width = Math.max(40, Number(bounds.width ?? 0) * scaleX);
            const height = Math.max(36, Number(bounds.height ?? 0) * scaleY);
            setPos({ left, top, width, height, scaleX, scaleY });
        };

        compute();
        const onUpdate = () => compute();
        window.addEventListener("updateHandlers", onUpdate as any);
        window.addEventListener("resize", onUpdate as any);
        return () => {
            window.removeEventListener("updateHandlers", onUpdate as any);
            window.removeEventListener("resize", onUpdate as any);
        };
    }, [editingClipId, clip?.id, CompositionStateStore.currentTime, selectedScope, sceneKey]);

    const close = () => {
        ApplicationStore.setEditingTextClipId(null);
    };

    const commit = () => {
        if (didCommitRef.current) return;
        didCommitRef.current = true;
        if (!clip || String(clip.type) !== "text") return close();
        const spans = CompositionService.clipSpansMultipleScenes(clip);
        const useScene = selectedScope === "scene" && spans && sceneKey;
        try {
            (clip as TextClip).updateTextForScene(useScene ? String(sceneKey) : null, value);
        } catch {}
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
        close();
    };

    const cancel = () => {
        didCommitRef.current = true;
        close();
    };

    // Reset commit guard when starting a new edit session.
    useEffect(() => {
        didCommitRef.current = false;
    }, [editingClipId]);

    // Commit when clicking anywhere outside the textarea (canvas clicks don't necessarily blur).
    useEffect(() => {
        if (!editingClipId || !isTextClip) return;
        const onPointerDownCapture = (e: PointerEvent) => {
            const t = e.target as Node | null;
            if (!t) return;
            if (textareaRef.current && textareaRef.current.contains(t)) return;
            commit();
        };
        window.addEventListener("pointerdown", onPointerDownCapture, true);
        return () => window.removeEventListener("pointerdown", onPointerDownCapture, true);
    }, [editingClipId, isTextClip, clip?.id, value, selectedScope, sceneKey]);

    if (!editingClipId || isCropMode || !isTextClip || !pos || !effectiveStyleAndText) return null;

    const effStyle = effectiveStyleAndText.style;
    const effAlign = effectiveStyleAndText.textAlign;

    const renderedHeight = editorHeightPx ?? pos.height;
    // Keep the editor centered on the underlying clip bounds as it grows/shrinks.
    const renderedTop = pos.top - (renderedHeight - pos.height) / 2;

    return (
        <textarea
            data-canvas-interactive="true"
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onMouseDown={(e) => {
                // Prevent deselection / timeline interactions while editing.
                e.stopPropagation();
            }}
            onKeyDown={(e) => {
                if (e.key === "Escape") {
                    e.preventDefault();
                    cancel();
                    return;
                }
                // Commit on Cmd/Ctrl+Enter (keeps Enter for newline).
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    commit();
                    return;
                }
            }}
            style={{
                position: "fixed",
                left: pos.left,
                top: renderedTop,
                width: pos.width,
                height: renderedHeight,
                zIndex: 60,
                border: "2px solid rgba(43, 98, 253, 0.9)",
                borderRadius: 6,
                padding: 0,
                boxSizing: "border-box",
                resize: "none",
                outline: "none",
                background: "rgba(255,255,255,0.06)",
                overflow: "hidden",
                color: String((effStyle as any)?.fillColor ?? "#FFFFFF"),
                fontFamily: String((effStyle as any)?.fontFamily ?? "Inter"),
                fontSize: Math.max(10, Number((effStyle as any)?.fontSize ?? 56) * (pos.scaleY || 1)),
                fontWeight: String((effStyle as any)?.fontWeight ?? "bold") as any,
                fontStyle: String((effStyle as any)?.fontStyle ?? "normal") as any,
                lineHeight: 1.2,
                textAlign: effAlign as any,
                boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            }}
        />
    );
});

export default TextClipEditorOverlay;
